<template>
    <div class="broadcast-request-viewer" :class="{ business: !!request.selectedTemplate }">
        <a-card class="card-container" :bordered="false">
            <a-card-meta :title="request.title" :description="request.description" class="mb-2 p-2 text-capitalize" />
            <div class="card-summary">
                <div class="card-summary-item">
                    <span class="details-subtitle">{{ $t("wp.broadcast.status") }} :</span>
                    <a-badge color="cyan" text="Created" v-if="request.status === 'created'" />
                    <a-badge color="volcano" text="Scheduled" v-if="request.status === 'scheduled'" />
                    <a-badge color="gray" text="Queued" v-if="request.status === 'queued'" />
                    <a-badge color="blue" text="Running" v-if="request.status === 'running'" />
                    <a-badge color="green" text="Completed" v-if="request.status === 'completed'" />
                    <a-badge color="red" text="Failed" v-if="request.status === 'failed'" />
                    <a-badge color="yellow" text="Cancelled" v-if="request.status === 'cancelled'" />
                </div>
                <div class="card-summary-item">
                    <span class="details-subtitle">{{ $t("wp.broadcast.progress") }} :</span>
                    <a-progress
                        :percent="getPercent()"
                        size="small"
                        status="normal"
                        v-if="request.status === 'created'"
                        v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                        :content="getProgressText(request)"
                    />
                    <a-progress
                        :percent="getPercent(request)"
                        size="small"
                        status="normal"
                        v-if="request.status === 'scheduled'"
                        v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                        :content="getProgressText(request)"
                    />
                    <a-progress
                        :percent="getPercent(request)"
                        size="small"
                        status="normal"
                        v-if="request.status === 'queued'"
                        v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                        :content="getProgressText(request)"
                    />
                    <a-progress
                        :percent="getPercent(request)"
                        size="small"
                        status="active"
                        v-if="request.status === 'running'"
                        v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                        :content="getProgressText(request)"
                    />
                    <a-progress
                        :percent="getPercent(request)"
                        size="small"
                        status="success"
                        v-if="request.status === 'completed'"
                        v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                        :content="getProgressText(request)"
                    />
                    <a-progress
                        :percent="getPercent(request)"
                        size="small"
                        status="exception"
                        v-if="request.status === 'failed'"
                        v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                        :content="getProgressText(request)"
                    />
                    <a-progress
                        :percent="getPercent(request)"
                        size="small"
                        status="exception"
                        v-if="request.status === 'cancelled'"
                        v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                        :content="getProgressText(request)"
                    />
                </div>
                <div class="card-summary-item">
                    <span class="details-subtitle">{{ $t("wp.broadcast.usageCost") }} :</span>
                    <span> {{ `${getUsageCost(request)} ₺` }}</span>
                </div>
                <div class="card-summary-item" v-if="['failed', 'cancelled'].includes(request.status)">
                    <span class="details-subtitle">Failed Reason:</span>
                    <span> {{ `${request.reason}` }}</span>
                    <a-button type="danger" @click="">
                        <div class="d-flex align-items-center justify-content-center">
                            <i class="material-icons">send</i>
                            Gönderilemeyen {{ getFailedCount() }} Kişiye Yeniden Gönder
                        </div>
                    </a-button>
                </div>
                <div class="card-summary-item">
                    <span class="details-subtitle">{{ $t("wp.broadcast.sendingTime") }} :</span>
                    <span v-if="!!request.scheduleAt">{{ scheduleAtHumanize(request) }}</span>
                    <span v-else>{{ createdAtHumanize(request) }}</span>
                </div>
            </div>
            <div class="card-main">
                <a-card :title="$t('wp.broadcast.customer')" class="details-card">
                    <div>
                        <a-divider orientation="left" class="details-subtitle"
                            >{{ $t("wp.broadcast.phoneNumbers") }}
                        </a-divider>

                        <div class="contacts">
                            <div class="contacts__line">
                                <span
                                    v-for="item in !isMoreContacts
                                        ? request.customPhoneNumbers.slice(0, 3)
                                        : request.customPhoneNumbers"
                                    class="number"
                                    :key="item._id"
                                    >{{ item.number }}</span
                                >
                            </div>
                            <div class="d-flex align-items-center flex-column mb-2">
                                <span
                                    v-if="
                                        !isMoreContacts &&
                                        !!request &&
                                        !!request.customPhoneNumbers &&
                                        request.customPhoneNumbers.length > 3
                                    "
                                    class="mb-1"
                                    >...</span
                                >
                                <a-button
                                    type="primary"
                                    @click="showMoreContact"
                                    v-if="
                                        !!request &&
                                        !!request.customPhoneNumbers &&
                                        request.customPhoneNumbers.length > 3
                                    "
                                    >{{ !isMoreContacts ? $t("wp.broadcast.showMore") : $t("wp.broadcast.showLess") }}
                                </a-button>
                            </div>
                        </div>
                    </div>

                    <div v-if="!!request && !!request.group && request.group.length > 0">
                        <a-divider orientation="left" class="details-subtitle"
                            >{{ $t("wp.broadcast.groupDetail") }}
                        </a-divider>
                        <div>
                            <div v-for="group in request.group">
                                <div>
                                    <span>{{ $t("wp.broadcast.groupName") }} :</span>
                                    <span>{{ group.name }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </a-card>
                <a-card :title="$t('wp.broadcast.messages')">
                    <div class="messages">
                        <WhatsappMessagePreview
                            :body="getTemplateParameters(request.selectedTemplate).body"
                            :footer="getTemplateParameters(request.selectedTemplate).footer"
                            :headerType="getTemplateParameters(request.selectedTemplate).headerText"
                            :header-text="getTemplateParameters(request.selectedTemplate).headerText"
                            :buttons="getTemplateParameters(request.selectedTemplate).buttons"
                            v-if="request.selectedTemplate"
                        />
                        <div class="body" v-else>
                            <div
                                v-for="(message, i) in request.messages"
                                class="payload-wrapper-message"
                                :key="message._id"
                            >
                                <span class="notallowed">
                                    <TextMessage
                                        v-model="message.text"
                                        v-if="message.type === 'TEXT'"
                                        :preview-mode="true"
                                        :show-attributes="false"
                                        :show-emoji="false"
                                        :disabled="true"
                                        :whatsapp-mode="false"
                                        :show-emoji-picker="false"
                                        :show-placeholder="false"
                                        class="not-allowed"
                                    />
                                    <RandomTextMessage
                                        v-else-if="message.type === 'RANDOM_TEXT'"
                                        :texts="message.texts"
                                        :preview-mode="true"
                                        :show-attributes="false"
                                        :show-emoji="false"
                                        :disabled="true"
                                        :whatsapp-mode="false"
                                        :show-emoji-picker="false"
                                        :show-placeholder="false"
                                    />
                                    <GalleryMessageEditor
                                        v-else-if="message.type === 'GALLERY'"
                                        :cards="message.cards"
                                        :preview-mode="true"
                                        :show-attributes="false"
                                        :show-emoji="false"
                                        :disabled="true"
                                        :whatsapp-mode="false"
                                        :show-emoji-picker="false"
                                        :show-placeholder="false"
                                        class="not-allowed"
                                    />
                                    <ImageBotResponse
                                        v-else-if="message.type === 'IMAGE'"
                                        :default-image-url="message.imageUrl"
                                        :default-asset-id="message.assetId"
                                        :preview-mode="true"
                                        :show-attributes="false"
                                        :show-emoji="false"
                                        :disabled="true"
                                        :whatsapp-mode="false"
                                        :show-emoji-picker="false"
                                        :show-placeholder="false"
                                        class="not-allowed"
                                    />

                                    <ButtonMessageEditor
                                        v-else-if="message.type === 'BUTTON'"
                                        v-model="request.messages[i]"
                                        :preview-mode="true"
                                        :show-attributes="false"
                                        :show-emoji="false"
                                        :disabled="true"
                                        :whatsapp-mode="false"
                                        :show-emoji-picker="false"
                                        :show-placeholder="false"
                                        class="not-allowed"
                                    />
                                    <QuickReplyMessageEditor
                                        v-else-if="message.type === 'QUICK_REPLY'"
                                        v-model="request.messages[i]"
                                        :preview-mode="true"
                                        :show-attributes="false"
                                        :show-emoji="false"
                                        :disabled="true"
                                        :whatsapp-mode="false"
                                        :show-emoji-picker="false"
                                        :show-placeholder="false"
                                        class="not-allowed"
                                    />
                                </span>
                            </div>
                        </div>
                    </div>
                </a-card>
            </div>
        </a-card>
    </div>
</template>
<script>
import Vue from "vue";
import QuickReplyMessageEditor from "../../../Chatbot/pages/dashboard/blocks/bot-response/QuickReplyMessageEditor.vue";
import ButtonMessageEditor from "../../../Chatbot/pages/dashboard/blocks/bot-response/ButtonMessageEditor.vue";
import ImageBotResponse from "../../../Chatbot/pages/dashboard/blocks/bot-response/ImageBotResponse.vue";
import GalleryMessageEditor from "../../../Chatbot/pages/dashboard/blocks/bot-response/GalleryMessageEditor.vue";
import RandomTextMessage from "../../../Chatbot/pages/dashboard/blocks/bot-response/RandomTextMessage.vue";
import TextMessage from "../../../Chatbot/pages/dashboard/blocks/bot-response/TextMessage.vue";
import moment from "moment/moment";
import WhatsappMessagePreview from "../../WhatsappMessagePreview.vue";
import _ from "lodash";
import WHATSAPP_API from "../../../../../api/whatsapp";
import { sendSocketMessage } from "../../../../../utils/socketStream";
import { mapState } from "vuex";

export default Vue.extend({
    name: "BroadcastRequestViewer",
    components: {
        WhatsappMessagePreview,
        TextMessage,
        RandomTextMessage,
        GalleryMessageEditor,
        ImageBotResponse,
        ButtonMessageEditor,
        QuickReplyMessageEditor,
    },
    props: {
        request: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            isMoreContacts: false,
        };
    },
    computed: {
        ...mapState({
            siteId: (state) => state.site.site._id,
            user: (state) => state.auth.user,
        }),
    },
    methods: {
        getTemplateParameters(template) {
            if (!template) {
                return {
                    headerType: null,
                    headerMediaType: null,
                    headerMediaURL: null,
                    headerText: null,
                    footer: null,
                    body: "",
                    buttons: [],
                };
            }

            // Meta format için (whatsapp-business-api-supsis)
            if (template.format === "meta" && template.components) {
                const components = template.components || [];
                const headerComponent = components.find((c) => c.type === "HEADER");
                const bodyComponent = components.find((c) => c.type === "BODY");
                const footerComponent = components.find((c) => c.type === "FOOTER");
                const buttonsComponent = components.find((c) => c.type === "BUTTONS");

                let headerMediaType = null;
                let headerMediaURL = null;
                let headerText = null;

                if (headerComponent) {
                    if (headerComponent.format === "TEXT") {
                        headerText = headerComponent.text;
                    } else if (["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComponent.format)) {
                        headerMediaType = headerComponent.format;
                        headerMediaURL = _.get(headerComponent, "example.header_handle[0]")
                            || _.get(template, "asset.url")
                            || _.get(template, "asset.cdnUrl");
                    }
                }

                return {
                    headerType: headerText ? "TEXT" : headerMediaType,
                    headerMediaType: headerMediaType,
                    headerMediaURL: headerMediaURL,
                    headerText: headerText,
                    footer: footerComponent ? footerComponent.text : null,
                    body: bodyComponent ? bodyComponent.text : "",
                    buttons: buttonsComponent ? buttonsComponent.buttons : [],
                };
            }

            // Gupshup format için (whatsapp-business-api)
            const metaObject = template.meta && typeof template.meta === "string"
                ? JSON.parse(template.meta)
                : template.meta || {};
            const containerMetaObj = template.containerMeta && typeof template.containerMeta === "string"
                ? JSON.parse(template.containerMeta)
                : template.containerMeta || {};

            return {
                headerType: null,
                headerMediaType: null,
                headerMediaURL: null,
                headerText: _.get(containerMetaObj, "header"),
                footer: _.get(containerMetaObj, "footer"),
                body: _.get(metaObject, "example") || metaObject.data || "",
                buttons: _.get(containerMetaObj, "buttons") || template.buttons || [],
            };
        },
        showMoreContact() {
            this.isMoreContacts = !this.isMoreContacts;
        },
        getUsageCost() {
            const { successMessageCount, basePricePerMessage, totalCost } = this.request;
            return (totalCost || (successMessageCount || 0) * (basePricePerMessage || 0)).toFixed(2);
        },
        getPercent() {
            const { total, sent, failed } = this.request;
            const totalSent = sent + failed;
            const percent = total !== 0 ? (totalSent / total) * 100 : 0;
            return parseFloat(percent.toFixed(2));
        },
        getProgressText() {
            const percent = this.getPercent();
            const { total, sent, failed } = this.request;
            return `${percent}% (${sent} sent/${total} contacts)`;
        },
        createdAtHumanize() {
            return moment(this.request.createdAt).format("DD MMM YYYY HH:mm");
        },
        scheduleAtHumanize() {
            return moment(this.request.scheduleAt).format("DD MMM YYYY HH:mm");
        },
        getFailedCount() {
            let { lastSentNumberIndex, customPhoneNumbers } = this.request;
            customPhoneNumbers = customPhoneNumbers || [];
            return customPhoneNumbers.length - lastSentNumberIndex;
        },
        resendForFailedNumbers() {
            const failedCount = this.getFailedCount();
            this.busy = true;
            const numbers = _.orderBy(this.request.customPhoneNumbers, ["number"], ["asc"]);
            const failedNumbers = numbers.slice(this.request.lastSentNumberIndex);
            const model = {
                title: `Yeniden Gönderim (${failedCount} kişi): ${this.request.title}`,
                description: this.request.description,
                messages: this.request.messages,
                all: this.request.all,
                groupId: null,
                customNumbers: failedNumbers,
                excludePhoneNumbers: this.request.excludePhoneNumbers,
                sendFutureAt: this.request.scheduleAt,
                channelId: typeof this.request.channel === "object" ? this.request.channel._id : this.request.channel,
                selectedTemplate: this.request.selectedTemplate,
                templateParams: this.request.templateParams,
            };
            WHATSAPP_API.BROADCAST.createBroadcastMessage(
                model.title,
                model.description,
                model.messages,
                model.all,
                model.groupId,
                model.excludePhoneNumbers,
                this.user._id,
                model.sendFutureAt,
                model.customNumbers,
                model.channelId,
                model.selectedTemplate,
                model.templateParams
            )
                .then((res) => {
                    const message = res.data;
                    sendSocketMessage("user whatsapp-broadcast-message-created", {
                        siteId: this.siteId,
                        broadcastId: message._id,
                    });
                    this.$message.success("Broadcast created successfully.");
                    this.$emit("newBroadcastCreated", message);
                })
                .catch((err) => {
                    this.$message.error("Broadcast could not be created.");
                })
                .finally(() => {
                    this.busy = false;
                });
        },
    },
});
</script>

<style scoped lang="scss">
.broadcast-request-viewer {
    display: flex;
    width: 100%;
    flex-direction: column;
    height: 100%;

    &.business {
        .messages {
            background: none !important;
            overflow: hidden;
        }
    }

    .card-summary {
        margin-bottom: 0.5rem;

        &-item {
            display: flex;
            flex-direction: row;
            flex-wrap: nowrap;
            align-items: center;
            padding: 0.5rem;

            span {
                flex: 0 0 25%;
                white-space: nowrap;
                color: #0d2226;
            }
        }

        &-item:nth-child(odd) {
            background-color: #e1e1e1;
            margin: 5px;
            border-radius: 2px;
        }
    }

    .ant-divider,
    .ant-divider-vertical {
        margin: 0 5px !important;
        width: 2px;
    }

    .card {
        margin-top: 0.5rem;
    }

    .card-main {
        display: flex;
        flex-direction: column;
    }

    @media screen and (min-width: 980px) {
        .card-main {
            flex-direction: row;
        }
    }

    @media screen and (min-width: 1300px) {
    }

    .contacts {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        max-height: 300px !important;
        overflow-y: scroll;
        margin-bottom: 0.5rem;

        &__line {
            display: flex;
            flex-direction: column;
            flex-wrap: wrap;

            .number {
                margin-bottom: 2px;
            }
        }
    }

    .not-allowed {
        cursor: not-allowed;
        user-select: none !important;
        pointer-events: none !important;

        .messages > * {
            cursor: not-allowed;
            user-select: none !important;
            pointer-events: none !important;
        }
    }

    .messages {
        .editable-editor-wrapper {
            cursor: not-allowed;
            user-select: none !important;
            pointer-events: none !important;
        }
    }

    .messages {
        //border: 1px solid red;
        display: flex;
        background: #dfdfdf;
        min-height: 200px;
        max-height: 400px !important;
        overflow-y: scroll;
        padding: 1rem 2rem;
        position: relative;

        .ProseMirror {
            height: 100% !important;
            width: 100% !important;
            border: 1px solid transparent !important;
        }

        .payload-wrapper-message {
            width: 100%;
            background: #dfdfdf;
            padding: 1rem;

            .payload-message:hover .attr-btn {
                opacity: 1;
                transform: translateY(0);
            }

            .attr-btn {
                border: none;
                background: #fff;
                position: absolute;
                bottom: -6px;
                right: 70px;
                border-radius: 50%;
                box-shadow: 0 0 6px #76767621;
                width: 32px;
                height: 32px;
                opacity: 0;
                transform: translateY(100%);
                transition: all 0.3s ease-in-out;
            }

            .actions-wrapper {
                display: flex;
                gap: 10px;
            }
        }

        .icon-preview-block-modal {
            font-size: 19px;
            padding-top: 1px;
        }

        .header {
            padding: 1rem 1.5rem 1rem 2rem;
            margin-left: 2rem;
            background: #fff;
            border-top-left-radius: 8px;
            border-top-right-radius: 8px;

            .left {
                .type {
                    color: #919ead;
                    font-weight: 600;
                    text-transform: uppercase;
                }

                .name {
                    font-weight: bold;
                    font-size: 1.5rem;
                }

                .custom-block-logo {
                    width: 24px;
                    height: 24px;
                }
            }

            .right {
                position: absolute;
                top: 0.5rem;
                right: 1rem;
                display: flex;
                gap: 0.5rem;

                .chatbot-btn {
                    border: none;
                    background: #dfdfdf;
                    border-radius: 4px;
                    padding: 4px 8px;
                    margin: 0;
                    min-height: 34px;
                    min-width: 34px;
                    color: #000;
                    display: flex;
                    justify-content: center;
                    align-items: center;

                    &.bordered {
                        border: 1px solid #000;
                    }
                }
            }
        }

        .body {
            //padding-left: 2rem;
            //max-height: calc(100vh - 140px);
            //overflow-y: scroll;
            width: 100%;

            &::-webkit-scrollbar {
                display: none;
            }

            .payload-wrapper-message {
                display: flex;
                flex-direction: column;
                background: #dfdfdf;
                padding: 1rem 0;
            }

            .payload {
                padding: 0 1.5rem;
                position: relative;
                border-bottom: 1px dashed #fff;
                margin: 10px 0;
                padding-bottom: 30px;

                input[type="checkbox"] {
                    display: none;

                    & + input {
                        display: none;
                    }

                    &:checked + input {
                        display: block;
                    }
                }

                textarea {
                    width: 100%;
                    resize: none;
                    height: 100px;
                    border: 1px solid transparent;
                    border-radius: 12px;
                    padding: 6px 12px;

                    &::-webkit-scrollbar {
                        display: none;
                    }

                    &:focus {
                        border-color: #06a9f3;
                    }
                }

                label {
                    display: flex;
                    gap: 1rem;
                    align-items: center;

                    span {
                        background: #888888;
                        padding: 0.4rem 0.7rem;
                        border-radius: 1rem;
                        color: #ffffff;
                        user-select: none;
                        transition: all 0.3s ease-in-out;
                        cursor: pointer;
                        min-width: 100px;
                        text-align: center;

                        &:hover {
                            background: #a2a2a2;
                        }
                    }

                    .attr-val {
                        width: 100%;
                        resize: none;
                        border: 1px solid #a2a2a2;
                        border-radius: 2px;
                        padding: 0.3rem 0.9rem;

                        &:focus {
                            border: 1px solid #0651f3;
                        }
                    }
                }

                .toolbar {
                    opacity: 0;
                    transition: all 0.3s ease-in-out;
                    position: absolute;
                    left: -17px;
                    bottom: 0;
                    top: 28px;
                    display: flex;
                    flex-direction: column;
                    //justify-content: center;
                    gap: 0.6rem;

                    .toolbar-btn {
                        border: none;
                        background: #ffffff;
                        border-radius: 50%;
                        margin: 0;
                        height: 34px;
                        width: 34px;
                        color: #000;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        box-shadow: 0 4px 20px #00000040;

                        &.bordered {
                            border: 1px solid #000;
                        }
                    }
                }

                &:hover .toolbar {
                    opacity: 1;
                }

                .chatbot-btn {
                    border: none;
                    background: #bd5555;
                    border-radius: 4px;
                    padding: 4px 8px;
                    margin: 0;
                    min-height: 34px;
                    min-width: 34px;
                    color: #000;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    position: absolute;
                    right: 10px;
                    top: 60px;

                    i {
                        color: #ffffff;
                    }

                    &.bordered {
                        border: 1px solid #000;
                    }
                }

                .keywords {
                    display: flex;
                    flex-wrap: wrap;
                    max-width: 430px;
                    gap: 0.4rem;
                    margin: 1rem 0;

                    .keyword {
                        border: 1px dashed #aaa;
                        border-radius: 1.5rem;
                        padding: 0.5rem 1rem;
                        background: transparent;
                        text-align: center;
                        transition: all 0.3s ease-in-out;
                        font-weight: bold;
                        max-width: 80px;

                        &::placeholder {
                            color: #000;
                        }

                        &:focus {
                            border: 1px solid;
                        }

                        &.filled:not(:focus) {
                            border: 1px solid #0651f3;
                            background-color: #0651f3;
                            color: #ffffff;
                        }
                    }
                }

                .says {
                    display: flex;
                    flex-direction: column;
                    gap: 0.4rem;
                    margin: 1rem 0;

                    .say {
                        width: 100%;
                        resize: none;
                        border: 1px solid transparent;
                        border-radius: 12px;
                        padding: 14px;

                        &:focus {
                            border: 1px solid #0651f3;
                        }
                    }
                }
            }

            .cbcard {
                background-color: #ffffff;
                border-radius: 4px;
                margin: 0 1.5rem;
                padding: 1rem;
            }

            .cbcard-btn {
                border: none;
                background: #fff;
                align-self: center;
                border-radius: 50%;
                height: 36px;
                transform: scale(0.7);

                &::before {
                    content: "";
                    width: 2px;
                    height: 1.9rem;
                    display: block;
                    z-index: -3;
                    position: absolute;
                    bottom: 100%;
                    left: 50%;
                    transform: translateX(-50%);
                    border: 1px dashed #888888;
                }
            }

            .condition-card {
                display: flex;
                margin-bottom: 1rem;
            }

            .cbcondition-btn {
                width: 100%;
                padding: 8px 15px;
                border-radius: 4px;
                border: 1px solid #dadada;
                transition: all 0.3s ease-in-out;
                background-color: #ffffff;

                &:hover {
                    border: 1px solid #06a9f3;
                    color: #06a9f3;
                }
            }
        }

        .ant-divider-horizontal.ant-divider-with-text-center::before,
        .ant-divider-horizontal.ant-divider-with-text-center::after {
            border-top: 1px solid #a8a8a8;
        }
    }
}
</style>
